import { CASE_PHASES, CASE_STATUSES, casePhaseIndex, datePl, formatDuration, type MeDto, type OwnerBoardCaseDto, type OwnerBoardDto, type OwnerBoardTaskDto } from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Group, Modal, Paper, Select, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconClockPause, IconFolderOpen, IconUser, IconUserCog } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { can } from '../auth';
import { PriorityBadge, TradeBadge } from '../components/Badges';
import { PageHeader } from '../components/PageHeader';
import { QueryState } from '../components/QueryState';
import { AssigneeLabel, DueBadge, TaskActions } from '../components/process/ProcessTasks';
import { EmptyState, FilterChips } from '../components/ui';
import { notifyError, notifyOk } from '../notify';
import { useOfficeUsers, useOwnerCandidates } from '../data';

type Filter = 'wszystkie' | 'utknely' | 'klient' | 'moje';
const TONE_COLOR = { late: 'red', todo: 'orange', wait: 'blue' } as const;
const ago = (iso: string) => formatDuration(Date.now() - Date.parse(iso));
const waitsForClient = (c: OwnerBoardCaseDto) => c.attention.some((a) => a.tone === 'wait');
const hasMine = (c: OwnerBoardCaseDto) => c.tasks.some((t) => t.mine);

/** Oś etapów: zaliczone, bieżący (z czasem w etapie), przyszłe. */
function PhaseTrack({ c }: { c: OwnerBoardCaseDto }) {
  const cur = casePhaseIndex(c.status);
  return (
    <Group gap={4} wrap="wrap">
      {CASE_PHASES.map((p, i) => {
        const done = i < cur;
        const now = i === cur;
        return (
          <Badge
            key={p.key}
            size="sm"
            radius="sm"
            variant={now ? 'filled' : done ? 'light' : 'outline'}
            color={now ? (c.stuck ? 'red' : 'eko') : done ? 'eko' : 'gray'}
            style={{ opacity: !now && !done ? 0.55 : 1, textTransform: 'none' }}
          >
            {p.label}
            {now ? ` · ${ago(c.statusSince)}` : ''}
          </Badge>
        );
      })}
    </Group>
  );
}

/** Zadanie na tablicy: tytuł, kto ma (albo kolejka), od kiedy, termin i działania — jak w „Moich zadaniach”. */
function TaskLine({ t }: { t: OwnerBoardTaskDto }) {
  return (
    <Group justify="space-between" gap="xs" wrap="wrap">
      <Group gap={8} wrap="wrap" style={{ minWidth: 0, flex: '1 1 300px' }}>
        <Text size="sm" fw={500}>
          {t.title}
        </Text>
        {t.mine ? (
          <Badge size="sm" variant="light" color="eko" leftSection={<IconUser size={11} />} style={{ textTransform: 'none' }}>
            Ty
          </Badge>
        ) : (
          <AssigneeLabel t={t} />
        )}
        <DueBadge t={t} />
        <Text size="xs" c="dimmed">
          od {ago(t.createdAt)}
        </Text>
      </Group>
      <TaskActions t={t} />
    </Group>
  );
}

/** Zmiana opiekuna sprawy (sprawy.zmiana_opiekuna). */
function OwnerModal({ c, currentOwnerId, onClose }: { c: OwnerBoardCaseDto; currentOwnerId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const users = useOwnerCandidates();
  const [ownerId, setOwnerId] = useState<string | null>(currentOwnerId);
  const m = useMutation({
    mutationFn: () => api(`/cases/${c.id}/owner`, { method: 'PUT', body: { ownerId } }),
    onSuccess: () => {
      notifyOk('Zmieniono opiekuna sprawy');
      void qc.invalidateQueries({ queryKey: ['owner-board'] });
      void qc.invalidateQueries({ queryKey: ['case', c.id] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Zmiana opiekuna sprawy">
      <Stack>
        <Text size="sm">
          {c.clientName} · {c.number}
        </Text>
        <Select label="Opiekun sprawy" description="Osoby z funkcją „opiekun” — mogą nadać sprawie bieg" data={(users.data ?? []).map((u) => ({ value: u.id, label: u.fullName }))} value={ownerId} onChange={setOwnerId} searchable allowDeselect={false} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!ownerId || ownerId === currentOwnerId}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CaseCard({ c, ownerId, canChangeOwner }: { c: OwnerBoardCaseDto; ownerId: string; canChangeOwner: boolean }) {
  const [owner, setOwner] = useState(false);
  return (
    <Card withBorder padding="sm" style={{ borderLeft: `4px solid var(--mantine-color-${c.stuck ? 'red-6' : waitsForClient(c) ? 'blue-4' : 'eko-5'})` }}>
      <Stack gap={8}>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Stack gap={2} style={{ minWidth: 0, flex: '1 1 280px' }}>
            <Group gap={6} wrap="wrap">
              <Anchor component={Link} to={`/sprawy/${c.id}`} fw={600}>
                {c.clientName}
              </Anchor>
              {c.tradeCodes.map((t) => (
                <TradeBadge key={t} code={t} />
              ))}
              <PriorityBadge priority={c.priority} />
            </Group>
            <Text size="xs" c="dimmed">
              {c.number} · {c.siteAddress}
            </Text>
          </Stack>
          <Stack gap={4} align="flex-end">
            <Text size="xs" c="dimmed" ta="right">
              {CASE_STATUSES[c.status]} · ostatni ruch {ago(c.lastActivityAt)} temu
            </Text>
            {canChangeOwner && (
              <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconUserCog size={13} />} onClick={() => setOwner(true)}>
                Zmień opiekuna
              </Button>
            )}
          </Stack>
        </Group>
        <PhaseTrack c={c} />
        {c.tasks.length > 0 ? (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Teraz u
            </Text>
            {c.tasks.map((t) => (
              <TaskLine key={t.id} t={t} />
            ))}
          </Stack>
        ) : (
          <Text size="sm" c={c.next ? undefined : 'dimmed'}>
            <Text span size="xs" c="dimmed" tt="uppercase" fw={600}>
              Następny krok:{' '}
            </Text>
            {c.next ? c.next.text : 'brak otwartych zadań — otwórz sprawę i zdecyduj, co dalej'}
            {c.next?.date ? ` (${datePl(c.next.date)})` : ''}
          </Text>
        )}
        {c.attention.length > 0 && (
          <Group gap={6} wrap="wrap">
            {c.attention.map((a, i) => (
              <Badge key={i} color={TONE_COLOR[a.tone]} variant={a.tone === 'late' ? 'light' : 'outline'} size="sm" style={{ textTransform: 'none' }} leftSection={a.tone === 'late' ? <IconAlertTriangle size={11} /> : a.tone === 'wait' ? <IconClockPause size={11} /> : undefined}>
                {a.text}
              </Badge>
            ))}
          </Group>
        )}
      </Stack>
      {owner && <OwnerModal c={c} currentOwnerId={ownerId} onClose={() => setOwner(false)} />}
    </Card>
  );
}

function Tile({ label, value, color, active, onClick, hint }: { label: string; value: number; color: string; active: boolean; onClick: () => void; hint: string }) {
  return (
    <UnstyledButton onClick={onClick}>
      <Paper withBorder p="sm" radius="md" style={{ borderColor: active ? `var(--mantine-color-${color}-6)` : undefined, borderWidth: active ? 2 : 1 }}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text fw={700} size="xl" c={value ? color : 'dimmed'}>
          {value}
        </Text>
        <Text size="xs" c="dimmed" lineClamp={2}>
          {hint}
        </Text>
      </Paper>
    </UnstyledButton>
  );
}

/** „Moje sprawy”: tablica opiekuna — etap każdej sprawy, kto ją teraz ma i gdzie utknęła. */
export function OwnerBoardPage({ me }: { me: MeDto }) {
  const supervisor = can(me, 'obieg.przydzielanie', 'W');
  const users = useOfficeUsers();
  const [ownerId, setOwnerId] = useState<string>(me.id);
  const [filter, setFilter] = useState<Filter>('wszystkie');
  const [phase, setPhase] = useState<string>('*');
  const q = useQuery({ queryKey: ['owner-board', ownerId], queryFn: () => api<OwnerBoardDto>(`/cases/owner-board?ownerId=${ownerId}`), refetchInterval: 60_000 });
  const all = q.data?.cases ?? [];
  const count = { utknely: all.filter((c) => c.stuck).length, klient: all.filter(waitsForClient).length, moje: all.filter(hasMine).length };
  const pick = (c: OwnerBoardCaseDto) =>
    (filter === 'wszystkie' || (filter === 'utknely' && c.stuck) || (filter === 'klient' && waitsForClient(c)) || (filter === 'moje' && hasMine(c))) && (phase === '*' || CASE_PHASES[casePhaseIndex(c.status)]?.key === phase);
  const list = all.filter(pick).sort((a, b) => Number(b.stuck) - Number(a.stuck) || casePhaseIndex(a.status) - casePhaseIndex(b.status));
  const self = ownerId === me.id;
  const toggle = (f: Filter) => setFilter((x) => (x === f ? 'wszystkie' : f));

  return (
    <>
      <PageHeader
        title={self ? 'Moje sprawy' : `Sprawy: ${q.data?.ownerName ?? ''}`}
        description={`Otwarte sprawy, ${self ? 'których jesteś opiekunem' : `których opiekunem jest ${q.data?.ownerName ?? 'wybrany pracownik'}`}: na jakim są etapie i od kiedy, u kogo jest teraz praca, na co czekają i gdzie utknęły.`}
        actions={
          supervisor && (
            <Select
              w={{ base: '100%', sm: 240 }}
              searchable
              data={(users.data ?? []).map((u) => ({ value: u.id, label: u.id === me.id ? `${u.fullName} (ja)` : u.fullName }))}
              value={ownerId}
              onChange={(v) => v && setOwnerId(v)}
              allowDeselect={false}
              aria-label="Opiekun"
            />
          )
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Stack>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Tile label="Otwarte sprawy" value={all.length} color="eko" active={filter === 'wszystkie'} onClick={() => setFilter('wszystkie')} hint="wszystkie etapy" />
            <Tile label="Utknęły" value={count.utknely} color="red" active={filter === 'utknely'} onClick={() => toggle('utknely')} hint="po terminie, nieobsadzone, bez ruchu" />
            <Tile label="Czekają na klienta" value={count.klient} color="blue" active={filter === 'klient'} onClick={() => toggle('klient')} hint="decyzja, podpis, płatność, wstrzymanie" />
            <Tile label={self ? 'Z Twoim zadaniem' : 'Z zadaniem opiekuna'} value={count.moje} color="orange" active={filter === 'moje'} onClick={() => toggle('moje')} hint={self ? 'do zrobienia przez Ciebie' : 'do zrobienia przez opiekuna'} />
          </SimpleGrid>
          <FilterChips
            value={phase}
            onChange={setPhase}
            options={[
              { value: '*', label: `Wszystkie etapy (${all.length})` },
              ...CASE_PHASES.map((p) => ({ value: p.key as string, label: `${p.label} (${all.filter((c) => CASE_PHASES[casePhaseIndex(c.status)]?.key === p.key).length})` })),
            ]}
          />
          {list.length ? (
            <Stack gap="xs">
              {list.map((c) => (
                <CaseCard key={c.id} c={c} ownerId={ownerId} canChangeOwner={!!q.data?.canChangeOwner} />
              ))}
            </Stack>
          ) : (
            <Card withBorder>
              <EmptyState
                icon={<IconFolderOpen size={24} />}
                title={all.length ? 'Brak spraw dla wybranego filtra' : 'Brak otwartych spraw'}
                hint={all.length ? 'Zmień filtr albo etap.' : self ? 'Sprawy, których jesteś opiekunem, pojawią się tutaj.' : 'Ten pracownik nie jest opiekunem żadnej otwartej sprawy.'}
              />
            </Card>
          )}
        </Stack>
      </QueryState>
    </>
  );
}
